import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    ArrowLeft, Camera, MapPinned, Loader2, Save, X, Edit2,
    MapPin, IdCard, GraduationCap, Calendar, ShieldCheck, AlertCircle,
    KeyRound, Trash2,
} from 'lucide-react';
import ResetPasswordModal from '@/components/ResetPasswordModal';
import ConfirmDialog from '@/components/ConfirmDialog';

const ACCENT = '#00875A';
const RFC_RE = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{2,3}$/i;

function formatDateTime(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function InspectorDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [geoLoading, setGeoLoading] = useState(false);
    const [photoError, setPhotoError] = useState('');
    const [form, setForm] = useState({});
    const [showResetPw, setShowResetPw] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [unlocking, setUnlocking] = useState(false);

    const unlock = async () => {
        setUnlocking(true);
        try {
            await api.post(`/admin/users/${id}/unlock`);
            toast.success('Cuenta desbloqueada');
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'No se pudo desbloquear'));
        } finally { setUnlocking(false); }
    };

    const load = () => {
        setLoading(true);
        api.get(`/admin/users/${id}`)
            .then((r) => { setData(r.data); setForm(r.data || {}); })
            .catch(() => toast.error('No se pudo cargar el auditor'))
            .finally(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [id]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onPhotoChange = (e) => {
        setPhotoError('');
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith('image/')) { setPhotoError('Sólo imágenes'); return; }
        if (f.size > 4 * 1024 * 1024) { setPhotoError('Máx. 4 MB'); return; }
        const reader = new FileReader();
        reader.onload = () => set('photo_url', reader.result);
        reader.readAsDataURL(f);
    };

    const captureGeo = () => {
        if (!navigator.geolocation) { toast.error('Geolocalización no soportada'); return; }
        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                set('latitude', parseFloat(pos.coords.latitude.toFixed(6)));
                set('longitude', parseFloat(pos.coords.longitude.toFixed(6)));
                toast.success('Ubicación capturada');
                setGeoLoading(false);
            },
            (err) => { toast.error(`Error GPS: ${err.message}`); setGeoLoading(false); },
            { enableHighAccuracy: true, timeout: 10000 },
        );
    };

    const startEdit = () => { setForm(data || {}); setEditing(true); };
    const cancelEdit = () => { setForm(data || {}); setEditing(false); setPhotoError(''); };

    const doDelete = async (force = false) => {
        setDeleting(true);
        try {
            await api.delete(`/admin/users/${id}${force ? '?force=true' : ''}`);
            toast.success('Auditor eliminado');
            navigate('/admin/inspectors');
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail || '';
            if (status === 409 && !force) {
                if (window.confirm(`${detail}\n\n¿Forzar eliminación de todos modos? Esta acción es irreversible.`)) {
                    await doDelete(true);
                    return;
                }
            } else {
                toast.error(apiErrorMessage(err, 'Error al eliminar'));
            }
        } finally { setDeleting(false); setShowDelete(false); }
    };

    const save = async () => {
        const rfc = (form.rfc || '').trim().toUpperCase();
        if (!rfc) { toast.error('El RFC es obligatorio'); return; }
        if (!RFC_RE.test(rfc)) {
            toast.error('RFC inválido (formato: 4 letras + 6 dígitos + 3 caracteres)');
            return;
        }
        setSaving(true);
        try {
            const lat = form.latitude !== '' && form.latitude !== null && !isNaN(parseFloat(form.latitude))
                ? parseFloat(form.latitude) : null;
            const lng = form.longitude !== '' && form.longitude !== null && !isNaN(parseFloat(form.longitude))
                ? parseFloat(form.longitude) : null;
            const patch = {
                name: form.name || '',
                phone: form.phone || null,
                rfc,
                education: form.education || null,
                photo_url: form.photo_url || null,
                address: form.address || null,
                city: form.city || null,
                state: form.state || null,
                country: form.country || null,
                latitude: lat,
                longitude: lng,
                notes: form.notes || null,
                active: !!form.active,
            };
            await api.patch(`/admin/users/${id}`, patch);
            toast.success('Cambios guardados');
            setEditing(false);
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al guardar'));
        } finally { setSaving(false); }
    };

    if (loading) return <AdminLayout title="Auditor"><div className="p-12 text-center text-gray-500">Cargando...</div></AdminLayout>;
    if (!data) return (
        <AdminLayout title="Auditor">
            <div className="p-12 text-center text-gray-500">
                <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-500" /> Auditor no encontrado
            </div>
        </AdminLayout>
    );

    const displayPhoto = editing ? form.photo_url : data.photo_url;
    const initial = (data.name || 'I').charAt(0).toUpperCase();

    return (
        <AdminLayout title={data.name}>
            <Link to="/admin/inspectors" className="inline-flex items-center gap-2 text-sm font-bold mb-5 hover:underline" style={{ color: ACCENT }}>
                <ArrowLeft className="w-4 h-4" /> Volver a Auditores
            </Link>

            {/* Header */}
            <div className="p-6 mb-6" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid="inspector-detail-header">
                <div className="flex items-start gap-5 flex-wrap">
                    <div className="relative shrink-0 w-28 h-28">
                        {displayPhoto ? (
                            <img key="photo" src={displayPhoto} alt={data.name || 'Auditor'}
                                className="w-28 h-28 object-cover" style={{ borderRadius: '4px' }} />
                        ) : (
                            <div key="initial" className="w-28 h-28 flex items-center justify-center font-display font-black text-4xl"
                                style={{ background: `${ACCENT}20`, color: ACCENT }}>
                                {initial}
                            </div>
                        )}
                        <label className="absolute -bottom-2 -right-2 cursor-pointer p-2 bg-white border-2 hover:bg-gray-50"
                            style={{ borderColor: ACCENT, display: editing ? 'block' : 'none' }}>
                            <Camera className="w-4 h-4" style={{ color: ACCENT }} />
                            <input type="file" accept="image/*" capture="user"
                                onChange={onPhotoChange} className="hidden"
                                data-testid="inspector-edit-photo" />
                        </label>
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <h1 className="font-display text-2xl font-black tracking-tight">{data.name}</h1>
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1"
                                style={{ background: data.active ? 'rgba(16,185,129,0.15)' : 'rgba(156,163,175,0.15)', color: data.active ? '#10B981' : '#9CA3AF' }}>
                                {data.active ? 'Activo' : 'Inactivo'}
                            </span>
                            {data.locked_at && (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1"
                                    style={{ background: 'rgba(220,38,38,0.15)', color: '#DC2626' }}
                                    data-testid="inspector-locked-badge">
                                    <AlertCircle className="w-3 h-3" /> Cuenta bloqueada
                                </span>
                            )}
                            <span className="text-xs px-2 py-1 font-bold uppercase tracking-wider" style={{ background: 'rgba(0,135,90,0.15)', color: ACCENT }}>Auditor</span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">{data.email}</div>
                        {data.rfc && <div className="text-xs font-mono text-gray-500 mt-1">RFC: {data.rfc}</div>}
                        {data.locked_at && (
                            <div className="text-xs text-red-600 mt-1" data-testid="inspector-locked-details">
                                Bloqueada el {formatDateTime(data.locked_at)} tras 5 intentos fallidos
                                {data.last_failed_ip && ` desde IP ${data.last_failed_ip}`}.
                            </div>
                        )}
                        {photoError && <div className="text-xs text-red-500 mt-2">{photoError}</div>}
                    </div>

                    <div className="flex gap-2 shrink-0 flex-wrap">
                        {data.locked_at && (
                            <button type="button" onClick={unlock} disabled={unlocking}
                                data-testid="unlock-inspector-btn"
                                style={{ display: editing ? 'none' : 'inline-flex' }}
                                className="items-center gap-2 px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 font-bold disabled:opacity-50">
                                {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                                Desbloquear
                            </button>
                        )}                        <button type="button" onClick={startEdit} data-testid="edit-inspector-btn"
                            style={{ display: editing ? 'none' : 'inline-flex', background: ACCENT }}
                            className="items-center gap-2 px-4 py-2 text-white font-bold">
                            <Edit2 className="w-4 h-4" /> Editar
                        </button>
                        <button type="button" onClick={() => setShowResetPw(true)}
                            data-testid="reset-inspector-password-btn"
                            style={{ display: editing ? 'none' : 'inline-flex', borderColor: 'var(--border)' }}
                            className="items-center gap-2 px-4 py-2 border font-bold hover:bg-black/5 dark:hover:bg-white/10">
                            <KeyRound className="w-4 h-4" /> Resetear contraseña
                        </button>
                        <button type="button" onClick={() => setShowDelete(true)}
                            data-testid="delete-inspector-btn"
                            style={{ display: editing ? 'none' : 'inline-flex' }}
                            className="items-center gap-2 px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 font-bold">
                            <Trash2 className="w-4 h-4" /> Eliminar
                        </button>
                        <button type="button" onClick={cancelEdit}
                            style={{ display: editing ? 'inline-flex' : 'none', borderColor: 'var(--border)' }}
                            className="items-center gap-2 px-4 py-2 border font-bold">
                            <X className="w-4 h-4" /> Cancelar
                        </button>
                        <button type="button" onClick={save} disabled={saving} data-testid="save-inspector-btn"
                            style={{ display: editing ? 'inline-flex' : 'none', background: ACCENT }}
                            className="items-center gap-2 px-4 py-2 text-white font-bold disabled:opacity-50">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            Guardar
                        </button>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card title="Datos generales" icon={IdCard}>
                    <Field label="Nombre" v={form.name} on={(v) => set('name', v)} edit={editing} placeholder="Nombre completo" />
                    <Field label="Correo" v={data.email} edit={false} />
                    <Field label="Teléfono" v={form.phone} on={(v) => set('phone', v)} edit={editing} placeholder="(+52) ..." />
                    <Field label="RFC *" v={form.rfc} on={(v) => set('rfc', (v || '').toUpperCase())} edit={editing} placeholder="PEMA850512H12" mono />
                    <ToggleField label="Activo" checked={editing ? !!form.active : !!data.active} on={(v) => set('active', v)} edit={editing} />
                </Card>

                <Card title="Formación académica" icon={GraduationCap}>
                    <textarea rows={5} value={(editing ? form.education : data.education) || ''}
                        onChange={(e) => set('education', e.target.value)}
                        readOnly={!editing}
                        data-testid="edit-education"
                        placeholder="Últimos estudios y certificaciones..."
                        className="w-full px-3 py-2 border outline-none resize-none text-sm"
                        style={{ borderColor: 'var(--border)', background: editing ? 'var(--card-bg)' : 'transparent', cursor: editing ? 'text' : 'default' }} />
                </Card>

                <Card title="Ubicación" icon={MapPin}>
                    <Field label="Dirección" v={editing ? form.address : data.address} on={(v) => set('address', v)} edit={editing} />
                    <div className="grid grid-cols-3 gap-2">
                        <Field label="Ciudad" v={editing ? form.city : data.city} on={(v) => set('city', v)} edit={editing} compact />
                        <Field label="Estado" v={editing ? form.state : data.state} on={(v) => set('state', v)} edit={editing} compact />
                        <Field label="País" v={editing ? form.country : data.country} on={(v) => set('country', v)} edit={editing} compact />
                    </div>
                </Card>

                <Card title="Geolocalización en tiempo real" icon={MapPinned}>
                    <Field label="Latitud" v={editing ? form.latitude : data.latitude} on={(v) => set('latitude', v)} edit={editing} />
                    <Field label="Longitud" v={editing ? form.longitude : data.longitude} on={(v) => set('longitude', v)} edit={editing} />
                    <button type="button" onClick={captureGeo} disabled={geoLoading}
                        style={{ display: editing ? 'inline-flex' : 'none', background: ACCENT }}
                        className="mt-2 text-white px-4 py-2 font-bold text-sm items-center gap-2 disabled:opacity-50">
                        {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPinned className="w-4 h-4" />}
                        {geoLoading ? 'Capturando...' : 'Capturar GPS'}
                    </button>
                    {data.latitude && data.longitude && (
                        <a href={`https://www.google.com/maps?q=${data.latitude},${data.longitude}`}
                            target="_blank" rel="noreferrer"
                            className="block text-sm font-bold mt-3 hover:underline" style={{ color: ACCENT }}>
                            Ver en Google Maps ↗
                        </a>
                    )}
                    {data.location_updated_at && (
                        <div className="text-xs text-gray-400 mt-2 inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Última actualización: {formatDateTime(data.location_updated_at)}
                        </div>
                    )}
                </Card>

                <Card title="Notas internas" icon={ShieldCheck} fullWidth>
                    <textarea rows={3} value={(editing ? form.notes : data.notes) || ''}
                        onChange={(e) => set('notes', e.target.value)}
                        readOnly={!editing}
                        className="w-full px-3 py-2 border outline-none resize-none text-sm"
                        style={{ borderColor: 'var(--border)', background: editing ? 'var(--card-bg)' : 'transparent' }} />
                </Card>
            </div>

            {showResetPw && (
                <ResetPasswordModal
                    userId={id}
                    userLabel={data.name || data.email}
                    accent={ACCENT}
                    onClose={() => setShowResetPw(false)}
                />
            )}
            {showDelete && (
                <ConfirmDialog
                    title="Eliminar auditor"
                    message={`¿Eliminar permanentemente a "${data.name}" (${data.email})? Esta acción es irreversible. Si tiene auditorías asociadas, se te preguntará si deseas forzar la eliminación.`}
                    confirmLabel="Eliminar"
                    tone="danger"
                    submitting={deleting}
                    onCancel={() => setShowDelete(false)}
                    onConfirm={() => doDelete(false)}
                />
            )}
        </AdminLayout>
    );
}

function Card({ title, icon: Icon, children, fullWidth }) {
    return (
        <div className={`p-6 ${fullWidth ? 'lg:col-span-2' : ''}`} style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
            <h3 className="font-display text-lg font-bold mb-4 inline-flex items-center gap-2">
                <Icon className="w-5 h-5" style={{ color: ACCENT }} /> {title}
            </h3>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

/**
 * DOM-stable field: always renders an <input>. ReadOnly when not editing, so React
 * never has to swap children types and avoids 'insertBefore NotFoundError' on React 19.
 */
function Field({ label, v, on, edit, placeholder, mono, compact }) {
    return (
        <div className={compact ? '' : 'flex items-start gap-3'}>
            <div className={`${compact ? 'text-[10px] mb-1' : 'text-xs w-32 pt-2 shrink-0'} uppercase tracking-wider font-bold text-gray-500`}>{label}</div>
            <div className="flex-1 min-w-0">
                <input type="text"
                    value={v ?? ''}
                    placeholder={edit ? placeholder : ''}
                    readOnly={!edit}
                    onChange={(e) => on && on(e.target.value)}
                    className={`w-full px-2 py-1.5 outline-none text-sm ${mono ? 'font-mono' : ''}`}
                    style={{
                        border: edit ? '1px solid var(--border)' : '1px solid transparent',
                        background: edit ? 'var(--card-bg)' : 'transparent',
                        cursor: edit ? 'text' : 'default',
                    }} />
            </div>
        </div>
    );
}

function ToggleField({ label, checked, on, edit }) {
    return (
        <div className="flex items-start gap-3">
            <div className="text-xs w-32 pt-2 shrink-0 uppercase tracking-wider font-bold text-gray-500">{label}</div>
            <div className="flex-1 min-w-0">
                <label className="inline-flex items-center gap-2 cursor-pointer py-2">
                    <input type="checkbox" checked={!!checked} disabled={!edit}
                        onChange={(e) => on && on(e.target.checked)} />
                    <span className="text-sm">{checked ? 'Activo' : 'Inactivo'}</span>
                </label>
            </div>
        </div>
    );
}
